import { useEffect } from 'react';
import Layout from './Layout';
import Hero from './Hero';
import RelatedGuides from './RelatedGuides';
import RelatedSearches from './RelatedSearches';
import StatsWrapper from '../utils/StatsWrapper';
import { REF_QUERY_PARAM } from '../../constants';

/**
 * The Phase 2 (hydrated) /search/{slug} page tree. Rendered by BOTH sides that must
 * agree for hydration to succeed without mismatches: entry-server.jsx (server, values
 * passed as explicit args) and SearchPage.jsx (client route, values read from the
 * embedded __INITIAL_DATA__ script tag). Kept as one component - not duplicated inline
 * in each caller - specifically so the two can't drift apart from each other over time
 * (docs/seo-ssr-search-pages-design.md §7.5 bundle-drift risk).
 */
function SearchResultsPage({ searchTerm, searchResults }) {
  // /search/{slug} pages are served directly by the Cloudflare Worker from KV, never
  // through Express's catch-all route - so the server-side referrer tracking there
  // (docs/ssr-seo-distribution-plan.md Phase 3 item 5) never runs for these pages, and
  // App.jsx's PageViewTracker doesn't either (SSR renders this tree standalone, not
  // through the App.jsx routing tree - see the module comment above). This is the only
  // page.view source these pages ever get, so - unlike the server-side referrer canary
  // it borrows its event name/shape from - it must fire unconditionally, not just when
  // ?r= is present: most real visits here are direct/organic Google clicks with no
  // referrer code at all, and gating on ?r= silently dropped every one of them. Runs
  // client-side only (useEffect never executes during SSR) - StatsWrapper.track() is
  // itself a no-op under SSR too (src/ssr/stubs/StatsWrapper.js), so a page that never
  // hydrates (JS-disabled, non-JS crawler) still won't double- or mis-report.
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get(REF_QUERY_PARAM);
    StatsWrapper.track('page.view', {
      page: 'search-results',
      searchTerm,
      ...(ref ? { referrer: ref } : {}),
    });
  }, [searchTerm]);

  return (
    // titleVariant="hero": Header renders the big homepage-style brand title here
    // (Hero itself no longer draws one) - same as the "/" route in App.jsx.
    <Layout showFooter={true} titleVariant="hero">
      <Hero initialSearchTerm={searchTerm} initialSearchResults={searchResults} />
      <RelatedGuides searchTerm={searchTerm} />
      <RelatedSearches searchTerm={searchTerm} />
    </Layout>
  );
}

export default SearchResultsPage;
